import {
  CHARACTER_FRAME,
  CHARACTER_IDS,
  DEFAULT_APPEARANCE,
  premadeNumber,
  type Appearance,
  type CharacterId,
  type PublicUser,
} from '@veriverse/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  CHARACTER_SHEETS,
  firstFrame,
  SHEET_FRAME,
  SHEET_ROWS,
  SHEET_SCALE,
  SHEET_TOP_PADDING,
} from '../game/characters.js';
import { api } from '../net/api.js';
import { useSessionStore } from '../state/session-store.js';

interface Props {
  user: PublicUser;
  /** Shown on the very first login, where saving is the only way forward. */
  firstTime?: boolean;
  onDone?: () => void;
}

const SCALE = 4;

/** Avatar editor: pick one of the premade characters (spec §6.8, ADR-0033). */
export function AppearanceEditor({ user, firstTime = false, onDone }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Appearance>(user.appearance ?? DEFAULT_APPEARANCE);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const save = async () => {
    setSaving(true);
    setFailed(false);
    try {
      const updated = await api<PublicUser>('/api/me/appearance', { method: 'PUT', body: draft });
      useSessionStore.getState().setUser(updated);
      onDone?.();
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="panel editor">
      <h1>{firstTime ? t('avatar.firstTitle') : t('avatar.title')}</h1>
      {firstTime && <p className="tagline">{t('avatar.firstIntro')}</p>}
      <div className="editor-body">
        <Preview appearance={draft} />
        <div className="slots">
          <fieldset>
            <legend>{t('avatar.character')}</legend>
            <div className="options">
              {CHARACTER_IDS.map((character) => {
                const selected = draft.character === character;
                const name = characterName(t, character);
                return (
                  <button
                    key={character}
                    type="button"
                    className={`option character-option${selected ? ' selected' : ''}`}
                    aria-pressed={selected}
                    title={name}
                    onClick={() => setDraft({ character })}
                  >
                    <CharacterThumb character={character} />
                    <span className="sr-only">{name}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>
        </div>
      </div>
      {failed && (
        <p role="alert" className="error">
          {t('avatar.saveFailed')}
        </p>
      )}
      <div className="row">
        <button type="button" disabled={saving} onClick={() => void save()}>
          {t('avatar.save')}
        </button>
        {!firstTime && (
          <button type="button" className="secondary" onClick={onDone}>
            {t('common.cancel')}
          </button>
        )}
      </div>
    </main>
  );
}

function characterName(t: TFunction, character: CharacterId): string {
  const number = premadeNumber(character);
  return number === undefined
    ? t(`avatar.characters.${character}`)
    : t('avatar.premadeCharacter', { number });
}

/**
 * Draws a premade character standing and facing down, cropped to the 32×48 character frame
 * exactly as the game shows it.
 */
function drawPremade(ctx: CanvasRenderingContext2D, image: HTMLImageElement): void {
  ctx.imageSmoothingEnabled = false;
  const height = SHEET_FRAME.height - SHEET_TOP_PADDING;
  ctx.drawImage(
    image,
    firstFrame('down') * SHEET_FRAME.width,
    SHEET_ROWS.idle * SHEET_FRAME.height + SHEET_TOP_PADDING,
    SHEET_FRAME.width,
    height,
    0,
    0,
    SHEET_FRAME.width * SHEET_SCALE,
    height * SHEET_SCALE,
  );
}

/** Calls `draw` once the character's sheet has loaded (immediately if cached). */
function withCharacterImage(character: CharacterId, draw: (image: HTMLImageElement) => void) {
  const url = CHARACTER_SHEETS[character];
  if (!url) return undefined;
  const image = new Image();
  let live = true;
  image.onload = () => {
    if (live) draw(image);
  };
  image.src = url;
  return () => {
    live = false;
  };
}

function CharacterThumb({ character }: { character: CharacterId }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(
    () =>
      withCharacterImage(character, (image) => {
        const ctx = ref.current?.getContext('2d');
        if (ctx) drawPremade(ctx, image);
      }),
    [character],
  );
  return (
    <canvas
      ref={ref}
      width={CHARACTER_FRAME.width}
      height={CHARACTER_FRAME.height}
      aria-hidden="true"
    />
  );
}

/** Live preview drawn from the same sheet the game uses. */
function Preview({ appearance }: { appearance: Appearance }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, CHARACTER_FRAME.width, CHARACTER_FRAME.height);
    return withCharacterImage(appearance.character, (image) => {
      ctx.clearRect(0, 0, CHARACTER_FRAME.width, CHARACTER_FRAME.height);
      drawPremade(ctx, image);
    });
  }, [appearance]);
  return (
    <canvas
      ref={ref}
      className="preview"
      width={CHARACTER_FRAME.width}
      height={CHARACTER_FRAME.height}
      style={{ width: CHARACTER_FRAME.width * SCALE, height: CHARACTER_FRAME.height * SCALE }}
      aria-hidden="true"
    />
  );
}
